import React from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Text, LoadingSpinner } from '../../components/ui'
import { supportCenterReportsService } from '../../services/supportCenterReports'
import type {
  SupportCenterDashboardFilters,
  SupportCenterVolumeMetrics,
} from '../../types/supportCenterReports'
import { APPOINTMENT_STATUS_LABELS } from '../../types/supportCenterReports'
import type { SupportCenterSpecialization } from '../../types/supportCenterSpecialties'
import type {
  SupportCenterAppointmentStatus,
  SupportCenterTriageStatus,
} from '../../types/supportCenterTriage'
import { TRIAGE_STATUS_LABELS } from '../../types/supportCenterTriage'
import {
  DASHBOARD_CHART_COLORS,
  emptyVolumeMetrics,
  formatDateBr,
  getErrorMessage,
} from '../../components/centralAmparo/dashboardHelpers'
import DashboardIndicadoresSection from './DashboardIndicadoresSection'

type DashboardVolumetriaTabProps = {
  filters: SupportCenterDashboardFilters
  specializations: SupportCenterSpecialization[]
  refreshToken: number
  active: boolean
  onLoadingChange: (loading: boolean) => void
}

type VolumetriaSection = 'atendimento' | 'triagens'

const APPOINTMENT_STATUS_STYLE: Record<
  SupportCenterAppointmentStatus,
  { gradient: string; hex: string }
> = {
  marcado: { gradient: 'from-sky-500 to-sky-600', hex: '#0ea5e9' },
  reagendado: { gradient: 'from-amber-500 to-amber-600', hex: '#f59e0b' },
  realizado: { gradient: 'from-emerald-500 to-emerald-600', hex: '#10b981' },
  cancelado: { gradient: 'from-red-500 to-red-600', hex: '#ef4444' },
  nao_realizado: { gradient: 'from-slate-500 to-slate-600', hex: '#64748b' },
  pre_agendado: { gradient: 'from-violet-500 to-violet-600', hex: '#8b5cf6' },
}

const KINSHIP_CHART_STYLE: Record<string, string> = {
  Titular: '#008B95',
  'Cônjuge': '#f59e0b',
  Filho: '#6366f1',
  'Não informado': '#64748b',
}

const CHANNEL_CHART_STYLE: Record<string, string> = {
  whatsapp: '#25D366',
  telefone: '#008B95',
  videochamada: '#8b5cf6',
  'whatsapp-bot': '#6366f1',
}

const TRIAGE_STATUS_STYLE: Record<SupportCenterTriageStatus, { gradient: string; hex: string }> = {
  pendente: { gradient: 'from-amber-500 to-amber-600', hex: '#f59e0b' },
  concluida: { gradient: 'from-emerald-500 to-emerald-600', hex: '#10b981' },
  reprovado: { gradient: 'from-red-500 to-red-600', hex: '#ef4444' },
  cancelado: { gradient: 'from-rose-500 to-rose-700', hex: '#e11d48' },
  nao_realizado: { gradient: 'from-slate-500 to-slate-600', hex: '#64748b' },
}

function formatCount(value: number): string {
  return value.toLocaleString('pt-BR')
}

function formatShare(count: number, total: number): string {
  if (total <= 0) return '0%'
  const rate = Math.round((count / total) * 1000) / 10
  return `${rate.toLocaleString('pt-BR')}%`
}

function KpiCard({
  label,
  value,
  percent,
  gradient,
}: {
  label: string
  value: number | string
  percent?: string
  gradient: string
}) {
  return (
    <div className={`bg-gradient-to-br ${gradient} shadow-lg rounded-lg p-4 text-white`}>
      <div className="text-center">
        <div className="flex items-baseline justify-center gap-2 mb-1">
          <Text size="2xl" className="text-white font-bold tabular-nums">
            {value}
          </Text>
          {percent ? (
            <Text size="sm" className="text-white/90 tabular-nums">
              {percent}
            </Text>
          ) : null}
        </div>
        <Text size="sm" className="text-white">
          {label}
        </Text>
      </div>
    </div>
  )
}

function ChartCard({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle?: string
  children: React.ReactNode
}) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-5 shadow">
      <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium mb-1">
        {title}
      </Text>
      {subtitle ? (
        <Text size="xs" color="muted" className="mb-3">
          {subtitle}
        </Text>
      ) : (
        <div className="mb-3" />
      )}
      {children}
    </div>
  )
}

function EmptyChart() {
  return (
    <div className="h-full flex items-center justify-center">
      <Text size="sm" color="muted">
        Sem dados para o período.
      </Text>
    </div>
  )
}

function DonutChart({
  data,
  tooltipLabel,
}: {
  data: Array<{ name: string; value: number; fill: string }>
  tooltipLabel: string
}) {
  if (data.length === 0) return <EmptyChart />
  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          innerRadius={52}
          outerRadius={80}
          paddingAngle={2}
          label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
        >
          {data.map((entry) => (
            <Cell key={entry.name} fill={entry.fill} />
          ))}
        </Pie>
        <Tooltip formatter={(value) => [Number(value), tooltipLabel]} />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  )
}

function SpecBarChart({
  data,
  seriesName,
}: {
  data: Array<{ name: string; total: number }>
  seriesName: string
}) {
  if (data.length === 0) return <EmptyChart />
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
        <YAxis allowDecimals={false} />
        <Tooltip />
        <Bar dataKey="total" fill="#008B95" name={seriesName} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  )
}

/** Barras horizontais para categorias com nomes longos (origem / demanda). */
function CatalogBarChart({
  data,
  seriesName,
}: {
  data: Array<{ name: string; total: number }>
  seriesName: string
}) {
  if (data.length === 0) return <EmptyChart />
  const chartHeight = Math.max(280, data.length * 36)
  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
      >
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} horizontal={false} />
        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
        <YAxis
          type="category"
          dataKey="name"
          width={140}
          tick={{ fontSize: 11 }}
          interval={0}
        />
        <Tooltip />
        <Bar dataKey="total" fill="#008B95" name={seriesName} radius={[0, 4, 4, 0]} />
      </BarChart>
    </ResponsiveContainer>
  )
}

function DemandTypeSpecialtyTabs({
  tabs,
  activeId,
  onChange,
}: {
  tabs: Array<{ id: string; name: string }>
  activeId: string | null
  onChange: (id: string) => void
}) {
  if (tabs.length === 0) return null
  return (
    <div className="border-b border-gray-200 dark:border-gray-700 mb-3">
      <nav className="-mb-px flex flex-wrap gap-x-6 gap-y-1">
        {tabs.map((tab) => {
          const selected = tab.id === activeId
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onChange(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                selected
                  ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {tab.name}
            </button>
          )
        })}
      </nav>
    </div>
  )
}

function DailyLineChart({
  data,
  seriesName,
}: {
  data: Array<{ date: string; total: number }>
  seriesName: string
}) {
  if (data.length === 0) return <EmptyChart />
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis
          dataKey="date"
          tick={{ fontSize: 11 }}
          interval="preserveStartEnd"
          minTickGap={24}
        />
        <YAxis allowDecimals={false} />
        <Tooltip />
        <Line
          type="monotone"
          dataKey="total"
          name={seriesName}
          stroke="#008B95"
          strokeWidth={2}
          dot={{ fill: '#008B95', r: 3 }}
          activeDot={{ r: 5 }}
        />
      </LineChart>
    </ResponsiveContainer>
  )
}

function SpecializationTable({
  rows,
  emptyLabel,
}: {
  rows: SupportCenterVolumeMetrics['by_specialization']
  emptyLabel: string
}) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
        <Text size="sm" className="text-gray-700 dark:text-gray-300 font-medium">
          Detalhe por especialidade
        </Text>
        <Text size="xs" color="muted">
          Totais consolidados dos filtros aplicados
        </Text>
      </div>
      {rows.length === 0 ? (
        <div className="px-4 py-10 text-center">
          <Text size="sm" color="muted">
            {emptyLabel}
          </Text>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Especialidade
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {rows.map((row) => (
                <tr key={row.specialization_id}>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{row.name}</td>
                  <td className="px-4 py-3 text-sm text-right">
                    <span className="inline-flex min-w-[2.5rem] justify-center rounded-full bg-primary-50 px-2.5 py-0.5 text-sm font-semibold text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                      {formatCount(row.total)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

const DashboardVolumetriaTab: React.FC<DashboardVolumetriaTabProps> = ({
  filters,
  specializations,
  refreshToken,
  active,
  onLoadingChange,
}) => {
  const [metrics, setMetrics] = React.useState<SupportCenterVolumeMetrics>(emptyVolumeMetrics)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [section, setSection] = React.useState<VolumetriaSection>('atendimento')
  const [demandSpecialtyTabId, setDemandSpecialtyTabId] = React.useState<string | null>(null)

  const loadVolume = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await supportCenterReportsService.getVolumeMetrics({
        date_from: filters.dateFrom || undefined,
        date_to: filters.dateTo || undefined,
        specialization_ids: filters.specializationIds.length ? filters.specializationIds : undefined,
        professional_user_ids: filters.professionalUserIds.length
          ? filters.professionalUserIds
          : undefined,
        attendant_email: filters.attendantEmail?.trim() || undefined,
        appointment_statuses: filters.appointmentStatuses.length
          ? filters.appointmentStatuses
          : undefined,
        triage_statuses: filters.triageStatuses.length ? filters.triageStatuses : undefined,
        channels: filters.channels.length ? filters.channels : undefined,
        patient_query: filters.patientQuery.trim() || undefined,
        policy_numbers: filters.policyNumbers.length ? filters.policyNumbers : undefined,
        convenio_codes: filters.convenioCodes.length ? filters.convenioCodes : undefined,
        assistance_ids: filters.assistanceIds.length ? filters.assistanceIds : undefined,
        referral_origin_ids: filters.referralOriginIds.length
          ? filters.referralOriginIds
          : undefined,
        demand_type_ids: filters.demandTypeIds.length ? filters.demandTypeIds : undefined,
      })
      setMetrics(data)
    } catch (e) {
      setError(getErrorMessage(e))
      setMetrics(emptyVolumeMetrics())
    } finally {
      setLoading(false)
    }
  }, [
    filters.dateFrom,
    filters.dateTo,
    filters.specializationIds,
    filters.professionalUserIds,
    filters.attendantEmail,
    filters.appointmentStatuses,
    filters.triageStatuses,
    filters.channels,
    filters.patientQuery,
    filters.policyNumbers,
    filters.convenioCodes,
    filters.assistanceIds,
    filters.referralOriginIds,
    filters.demandTypeIds,
  ])

  React.useEffect(() => {
    void loadVolume()
  }, [loadVolume, refreshToken])

  React.useEffect(() => {
    if (active) onLoadingChange(loading)
  }, [active, loading, onLoadingChange])

  const statusChartData = (
    Object.keys(APPOINTMENT_STATUS_LABELS) as SupportCenterAppointmentStatus[]
  )
    .map((key) => ({
      name: APPOINTMENT_STATUS_LABELS[key],
      value: metrics.by_status[key],
      fill: APPOINTMENT_STATUS_STYLE[key].hex,
    }))
    .filter((item) => item.value > 0)

  const specializationChartData = metrics.by_specialization.map((item) => ({
    name: item.name,
    total: item.total,
  }))

  const dayChartData = metrics.by_day.map((item) => ({
    date: formatDateBr(item.date),
    total: item.total,
  }))

  const kinshipChartData = metrics.by_kinship.map((item, index) => ({
    name: item.name,
    value: item.total,
    fill: KINSHIP_CHART_STYLE[item.name] ?? DASHBOARD_CHART_COLORS[index % DASHBOARD_CHART_COLORS.length],
  }))

  const channelChartData = (metrics.by_channel ?? []).map((item) => ({
    name: item.name,
    value: item.total,
    fill: CHANNEL_CHART_STYLE[item.channel] ?? '#64748b',
  }))

  const demandSpecialtyTabs = React.useMemo(() => {
    const selectedIds = filters.specializationIds
    const catalog =
      selectedIds.length > 0
        ? specializations.filter((spec) => selectedIds.includes(spec.id))
        : specializations
    return catalog
      .map((spec) => ({ id: spec.id, name: spec.name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }))
  }, [filters.specializationIds, specializations])

  React.useEffect(() => {
    if (demandSpecialtyTabs.length === 0) {
      setDemandSpecialtyTabId(null)
      return
    }
    setDemandSpecialtyTabId((prev) =>
      prev && demandSpecialtyTabs.some((tab) => tab.id === prev)
        ? prev
        : demandSpecialtyTabs[0].id
    )
  }, [demandSpecialtyTabs])

  const attendanceDemandBySpecMap = React.useMemo(() => {
    const map = new Map<string, Array<{ name: string; total: number }>>()
    for (const row of metrics.attendance_by_demand_type_by_specialization ?? []) {
      map.set(
        row.specialization_id,
        row.items.map((item) => ({ name: item.name, total: item.total }))
      )
    }
    return map
  }, [metrics.attendance_by_demand_type_by_specialization])

  const attendanceDemandTypeChartData = demandSpecialtyTabId
    ? (attendanceDemandBySpecMap.get(demandSpecialtyTabId) ?? [])
    : []

  const triageDemandBySpecMap = React.useMemo(() => {
    const map = new Map<string, Array<{ name: string; total: number }>>()
    for (const row of metrics.triage_by_demand_type_by_specialization ?? []) {
      map.set(
        row.specialization_id,
        row.items.map((item) => ({ name: item.name, total: item.total }))
      )
    }
    return map
  }, [metrics.triage_by_demand_type_by_specialization])

  const demandTypeChartData = demandSpecialtyTabId
    ? (triageDemandBySpecMap.get(demandSpecialtyTabId) ?? [])
    : []

  const triageStatusChartData = (
    Object.keys(TRIAGE_STATUS_LABELS) as SupportCenterTriageStatus[]
  )
    .map((key) => ({
      name: TRIAGE_STATUS_LABELS[key],
      value: metrics.triage_by_status[key],
      fill: TRIAGE_STATUS_STYLE[key].hex,
    }))
    .filter((item) => item.value > 0)

  const triageSpecializationChartData = (metrics.triage_by_specialization ?? []).map((item) => ({
    name: item.name,
    total: item.total,
  }))

  const triageDayChartData = (metrics.triage_by_day ?? []).map((item) => ({
    date: formatDateBr(item.date),
    total: item.total,
  }))

  const referralOriginChartData = (metrics.triage_by_referral_origin ?? []).map((item) => ({
    name: item.name,
    total: item.total,
  }))

  const triageTotal =
    metrics.triage_by_status.pendente +
    metrics.triage_by_status.concluida +
    metrics.triage_by_status.reprovado +
    metrics.triage_by_status.cancelado +
    metrics.triage_by_status.nao_realizado

  const sectionTabClass = (tab: VolumetriaSection) =>
    `py-3 px-1 border-b-2 font-medium text-sm ${
      section === tab
        ? 'border-primary-600 text-primary-600 dark:text-primary-400'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
    }`

  if (!active) return null

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <Text size="sm">{error}</Text>
        </div>
      )}

      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          <button
            type="button"
            onClick={() => setSection('atendimento')}
            className={sectionTabClass('atendimento')}
          >
            Atendimento
          </button>
          <button
            type="button"
            onClick={() => setSection('triagens')}
            className={sectionTabClass('triagens')}
          >
            Triagens
          </button>
        </nav>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      ) : section === 'atendimento' ? (
        <div className="space-y-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <KpiCard
              label="Total"
              value={formatCount(metrics.total)}
              gradient="from-primary-500 to-primary-600"
            />
            <KpiCard
              label="Marcados"
              value={formatCount(metrics.by_status.marcado)}
              percent={formatShare(metrics.by_status.marcado, metrics.total)}
              gradient={APPOINTMENT_STATUS_STYLE.marcado.gradient}
            />
            <KpiCard
              label="Reagendados"
              value={formatCount(metrics.by_status.reagendado)}
              percent={formatShare(metrics.by_status.reagendado, metrics.total)}
              gradient={APPOINTMENT_STATUS_STYLE.reagendado.gradient}
            />
            <KpiCard
              label="Realizados"
              value={formatCount(metrics.by_status.realizado)}
              percent={formatShare(metrics.by_status.realizado, metrics.total)}
              gradient={APPOINTMENT_STATUS_STYLE.realizado.gradient}
            />
            <KpiCard
              label="Cancelados"
              value={formatCount(metrics.by_status.cancelado)}
              percent={formatShare(metrics.by_status.cancelado, metrics.total)}
              gradient={APPOINTMENT_STATUS_STYLE.cancelado.gradient}
            />
            <KpiCard
              label="Não realizados"
              value={formatCount(metrics.by_status.nao_realizado)}
              percent={formatShare(metrics.by_status.nao_realizado, metrics.total)}
              gradient={APPOINTMENT_STATUS_STYLE.nao_realizado.gradient}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard title="Atendimentos por status" subtitle="Distribuição do volume filtrado">
              <div className="h-72">
                <DonutChart data={statusChartData} tooltipLabel="Atendimentos" />
              </div>
            </ChartCard>
            <ChartCard
              title="Volumetria por especialidade"
              subtitle="Total de atendimentos no período"
            >
              <div className="h-72">
                <SpecBarChart data={specializationChartData} seriesName="Atendimentos" />
              </div>
            </ChartCard>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard title="Volume diário" subtitle="Atendimentos por dia no período">
              <div className="h-72">
                <DailyLineChart data={dayChartData} seriesName="Atendimentos" />
              </div>
            </ChartCard>
            <ChartCard
              title="Atendimentos por parentesco"
              subtitle="Titular, cônjuge e filho no período"
            >
              <div className="h-72">
                <DonutChart data={kinshipChartData} tooltipLabel="Atendimentos" />
              </div>
            </ChartCard>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SpecializationTable
              rows={metrics.by_specialization}
              emptyLabel="Nenhum atendimento no período."
            />
            <ChartCard
              title="Volumetria por canal"
              subtitle="Distribuição por canal de agendamento"
            >
              <div className="h-72">
                <DonutChart
                  data={channelChartData.some((item) => item.value > 0) ? channelChartData : []}
                  tooltipLabel="Atendimentos"
                />
              </div>
            </ChartCard>
          </div>

          <ChartCard
            title="Tipo de demanda"
            subtitle="Distribuição dos atendimentos por tipo de demanda enviado"
          >
            <DemandTypeSpecialtyTabs
              tabs={demandSpecialtyTabs}
              activeId={demandSpecialtyTabId}
              onChange={setDemandSpecialtyTabId}
            />
            <div className="min-h-72 overflow-x-auto">
              <CatalogBarChart data={attendanceDemandTypeChartData} seriesName="Atendimentos" />
            </div>
          </ChartCard>

          <DashboardIndicadoresSection metrics={metrics.strategic} />
        </div>
      ) : (
        <div className="space-y-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <KpiCard
              label="Total"
              value={formatCount(triageTotal)}
              gradient="from-primary-500 to-primary-600"
            />
            <KpiCard
              label="Pendentes"
              value={formatCount(metrics.triage_by_status.pendente)}
              percent={formatShare(metrics.triage_by_status.pendente, triageTotal)}
              gradient={TRIAGE_STATUS_STYLE.pendente.gradient}
            />
            <KpiCard
              label="Concluídas"
              value={formatCount(metrics.triage_by_status.concluida)}
              percent={formatShare(metrics.triage_by_status.concluida, triageTotal)}
              gradient={TRIAGE_STATUS_STYLE.concluida.gradient}
            />
            <KpiCard
              label="Reprovados"
              value={formatCount(metrics.triage_by_status.reprovado)}
              percent={formatShare(metrics.triage_by_status.reprovado, triageTotal)}
              gradient={TRIAGE_STATUS_STYLE.reprovado.gradient}
            />
            <KpiCard
              label="Cancelados"
              value={formatCount(metrics.triage_by_status.cancelado)}
              percent={formatShare(metrics.triage_by_status.cancelado, triageTotal)}
              gradient={TRIAGE_STATUS_STYLE.cancelado.gradient}
            />
            <KpiCard
              label="Não realizadas"
              value={formatCount(metrics.triage_by_status.nao_realizado)}
              percent={formatShare(metrics.triage_by_status.nao_realizado, triageTotal)}
              gradient={TRIAGE_STATUS_STYLE.nao_realizado.gradient}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard title="Triagens por status" subtitle="Distribuição das triagens no período">
              <div className="h-72">
                <DonutChart data={triageStatusChartData} tooltipLabel="Triagens" />
              </div>
            </ChartCard>
            <ChartCard
              title="Volumetria por especialidade"
              subtitle="Total de triagens no período"
            >
              <div className="h-72">
                <SpecBarChart data={triageSpecializationChartData} seriesName="Triagens" />
              </div>
            </ChartCard>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard
              title="Origem do encaminhamento"
              subtitle="Distribuição das triagens por origem"
            >
              <div className="min-h-72 overflow-x-auto">
                <CatalogBarChart data={referralOriginChartData} seriesName="Triagens" />
              </div>
            </ChartCard>
            <ChartCard
              title="Tipo de demanda"
              subtitle="Distribuição das triagens por demanda percebida"
            >
              <DemandTypeSpecialtyTabs
                tabs={demandSpecialtyTabs}
                activeId={demandSpecialtyTabId}
                onChange={setDemandSpecialtyTabId}
              />
              <div className="min-h-72 overflow-x-auto">
                <CatalogBarChart data={demandTypeChartData} seriesName="Triagens" />
              </div>
            </ChartCard>
          </div>

          <ChartCard title="Volume diário" subtitle="Triagens por dia no período">
            <div className="h-72">
              <DailyLineChart data={triageDayChartData} seriesName="Triagens" />
            </div>
          </ChartCard>

          <SpecializationTable
            rows={metrics.triage_by_specialization ?? []}
            emptyLabel="Nenhuma triagem no período."
          />
        </div>
      )}
    </div>
  )
}

export default DashboardVolumetriaTab
